// pages/ChatbotAnalytics.tsx
import React, { useState, useEffect } from 'react';
import { MessageCircle, TrendingUp, Users, Clock, Star, BarChart3, Calendar } from 'lucide-react';
import { chatbotAnalytics, type ChatbotStats } from '../modules/chatbot/services/chatbotAnalytics';

export const ChatbotAnalytics: React.FC = () => {
  const [stats, setStats] = useState<ChatbotStats | null>(null);
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'all'>('week');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const data = await chatbotAnalytics.getStats(period);
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-orange-500" />
            Analityka Chatbota AI
          </h1>
          <p className="text-gray-400 mt-1">
            Monitoruj wydajność i skuteczność czatbota
          </p>
        </div>
        
        {/* Wybór okresu */}
        <div className="flex gap-2 bg-zinc-800 p-1 rounded-lg">
          {(['today', 'week', 'month', 'all'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg transition-all ${
                period === p 
                  ? 'bg-orange-500 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-zinc-700'
              }`}
            >
              {p === 'today' ? 'Dzisiaj' : 
               p === 'week' ? 'Tydzień' : 
               p === 'month' ? 'Miesiąc' : 'Wszystko'}
            </button>
          ))}
        </div>
      </div>

      {/* Główne metryki */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <div className="flex items-center justify-between mb-4">
            <MessageCircle className="w-8 h-8 text-blue-500" />
            <span className="text-2xl font-bold text-white">{stats.totalConversations}</span>
          </div>
          <h3 className="text-gray-400 text-sm">Wszystkie rozmowy</h3>
          <p className="text-green-500 text-sm mt-2">
            +{stats.activeConversations} aktywnych
          </p>
        </div>

        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <div className="flex items-center justify-between mb-4">
            <Users className="w-8 h-8 text-green-500" />
            <span className="text-2xl font-bold text-white">{stats.totalLeads}</span>
          </div>
          <h3 className="text-gray-400 text-sm">Nowe leady</h3>
          <p className="text-green-500 text-sm mt-2">
            {stats.conversionRate.toFixed(1)}% konwersji
          </p>
        </div>

        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <div className="flex items-center justify-between mb-4">
            <Clock className="w-8 h-8 text-purple-500" />
            <span className="text-2xl font-bold text-white">
              {stats.averageResponseTime.toFixed(1)}s
            </span>
          </div>
          <h3 className="text-gray-400 text-sm">Śr. czas odpowiedzi</h3>
          <p className="text-gray-500 text-sm mt-2">
            {stats.averageConversationDuration.toFixed(0)} min rozmowy
          </p>
        </div>

        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <div className="flex items-center justify-between mb-4">
            <Star className="w-8 h-8 text-yellow-500" />
            <span className="text-2xl font-bold text-white">{stats.satisfactionRate}%</span>
          </div>
          <h3 className="text-gray-400 text-sm">Satysfakcja</h3>
          <p className="text-gray-500 text-sm mt-2">
            Ocena użytkowników
          </p>
        </div>
      </div>

      {/* Wykres konwersacji */}
      <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-orange-500" />
          Aktywność czatbota
        </h2>
        <div className="h-64 flex items-center justify-center text-gray-500">
          {/* TODO: Dodać wykres z recharts */}
          <p>Wykres aktywności (wkrótce)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popularne pytania */}
        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <h2 className="text-xl font-semibold mb-4">Najczęstsze pytania</h2>
          <div className="space-y-3">
            {stats.popularQuestions.map((q, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-gray-300 truncate flex-1">{q.question}</span>
                <span className="text-orange-500 font-semibold ml-4">{q.count}</span>
              </div>
            ))}
            {stats.popularQuestions.length === 0 && (
              <p className="text-gray-500 text-center py-4">Brak danych</p>
            )}
          </div>
        </div>

        {/* Ostatnie konwersacje */}
        <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700">
          <h2 className="text-xl font-semibold mb-4">Ostatnie rozmowy</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-zinc-700 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="font-medium">Jan Kowalski</p>
                  <p className="text-xs text-gray-400">5 minut temu</p>
                </div>
              </div>
              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
                Nowy lead
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-zinc-700 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium">Anna Nowak</p>
                  <p className="text-xs text-gray-400">15 minut temu</p>
                </div>
              </div>
              <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                W trakcie
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Zalecenia AI */}
      <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 rounded-xl p-6 border border-orange-500/20">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-orange-500" />
          Zalecenia AI
        </h2>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
            <div>
              <p className="text-gray-300">
                Najczęściej zadawane pytanie dotyczy cen - rozważ dodanie cennika na stronie
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
            <div>
              <p className="text-gray-300">
                Średni czas konwersacji wynosi {stats.averageConversationDuration.toFixed(0)} minut - to dobry wynik
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
            <div>
              <p className="text-gray-300">
                Współczynnik konwersji {stats.conversionRate.toFixed(1)}% - spróbuj bardziej angażujących wiadomości powitalnych
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};